import { toWesternDigits } from '@horof/shared';

/**
 * Reading a bank statement pasted from Excel or saved as CSV, for arqam's
 * `appImportBankStatement` (its doc 57). Only reading: arqam checks that the
 * lines add up, finds duplicates, and computes every total.
 */

/** Cells of each row; tab (pasted from Excel), «;» or «,» (CSV). */
export function splitRows(text: string): string[][] {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);
  const sample = lines.slice(0, 5).join('\n');
  const delimiter = sample.includes('\t')
    ? '\t'
    : count(sample, ';') >= count(sample, ',')
      ? ';'
      : ',';
  return lines
    .filter((l) => l.trim() !== '')
    .map((l) => splitLine(l, delimiter).map((c) => c.trim()));
}

const count = (s: string, c: string) => s.split(c).length - 1;

/** One CSV line, with "quoted ""cells""" that may hold the delimiter. */
function splitLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"' && cell.trim() === '') {
      quoted = true;
      cell = '';
    } else if (c === delimiter) {
      cells.push(cell);
      cell = '';
    } else cell += c;
  }
  cells.push(cell);
  return cells;
}

/**
 * An amount as banks write it, signed, for the API ("-1234.50"), or null.
 * "1 234,50", "1.234,50", "1,234.50", "-500", "500-", "(500)", "500 DH".
 * Empty: null.
 */
export function bankAmount(text: string): string | null {
  let t = toWesternDigits(text)
    .replace(/(MAD|DH|DHS|درهم)/gi, '')
    .replace(/[\s  ']/g, '')
    .replace(/[−–]/g, '-');
  if (!t) return null;
  let negative = false;
  if (/^\(.*\)$/.test(t)) {
    negative = true;
    t = t.slice(1, -1);
  }
  if (t.endsWith('-')) {
    negative = true;
    t = t.slice(0, -1);
  }
  if (t.startsWith('-')) {
    negative = !negative;
    t = t.slice(1);
  } else if (t.startsWith('+')) t = t.slice(1);
  // The last «,» or «.» followed by 1–2 digits is the decimal point; the
  // others separate thousands.
  const decimal = t.match(/[.,٫](\d{1,2})$/);
  const int = (decimal ? t.slice(0, -decimal[0].length) : t).replace(
    /[.,]/g,
    '',
  );
  if (!/^\d+$/.test(int)) return null;
  const cents = (decimal?.[1] ?? '').padEnd(2, '0');
  if (Number(int) === 0 && Number(cents) === 0) return null;
  return `${negative ? '-' : ''}${Number(int)}.${cents}`;
}

/** "2026-03-05", "05/03/2026", "5-3-2026", "05.03.26" → "2026-03-05". */
export function bankDate(text: string): string | null {
  const t = toWesternDigits(text).trim().split(/[\sT]/)[0];
  let y: number;
  let m: number;
  let d: number;
  const iso = t.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  const fr = t.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
  if (iso) [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else if (fr) {
    [d, m, y] = [Number(fr[1]), Number(fr[2]), Number(fr[3])];
    if (y < 100) y += 2000;
  } else return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return date.toISOString().slice(0, 10);
}

/** Which column holds what; -1 = none. */
export type Mapping = {
  header: boolean;
  date: number;
  label: number;
  /** One signed column… */
  amount: number;
  /** …or two: money out and money in. */
  debit: number;
  credit: number;
  balance: number;
  reference: number;
};

const HEADERS: [keyof Omit<Mapping, 'header'>, RegExp][] = [
  ['date', /^date|date op|تاريخ/i],
  ['label', /libell|label|d[ée]signation|description|op[ée]ration|بيان/i],
  ['debit', /d[ée]bit|مدين|سحب|خروج/i],
  ['credit', /cr[ée]dit|دائن|إيداع|دخول/i],
  ['amount', /montant|amount|مبلغ/i],
  ['balance', /solde|balance|رصيد/i],
  ['reference', /r[ée]f|مرجع/i],
];

/** A first guess from the header row, else from what the cells look like. */
export function guessMapping(rows: string[][]): Mapping {
  const map: Mapping = {
    header: false,
    date: -1,
    label: -1,
    amount: -1,
    debit: -1,
    credit: -1,
    balance: -1,
    reference: -1,
  };
  const first = rows[0] ?? [];
  first.forEach((cell, i) => {
    const found = HEADERS.find(([key, re]) => map[key] === -1 && re.test(cell));
    if (found) map[found[0]] = i;
  });
  map.header = map.date !== -1 || map.label !== -1;
  if (map.header) {
    if (map.debit !== -1 && map.credit !== -1) map.amount = -1;
    return map;
  }
  // No header: date, then the longest text, then the first amount.
  const row = first;
  map.date = row.findIndex((c) => bankDate(c));
  map.amount = row.findIndex(
    (c, i) => i !== map.date && /\d/.test(c) && bankAmount(c),
  );
  let longest = -1;
  row.forEach((c, i) => {
    if (i !== map.date && i !== map.amount && !bankAmount(c)) {
      if (longest === -1 || c.length > row[longest].length) longest = i;
    }
  });
  map.label = longest;
  return map;
}

export type StatementLine = {
  date: string;
  label: string;
  amount: string;
  balance?: string;
  reference?: string;
};

/** A row without a readable date: a total or a balance line, most often. */
export const UNDATED = 'تاريخ غير مقروء';

export type ReadLine = {
  /** Position in the pasted text, from 1 (header included). */
  row: number;
  cells: string[];
  line: StatementLine | null;
  problem: string | null;
};

/** Each row as a statement line, or what is wrong with it. */
export function readLines(rows: string[][], map: Mapping): ReadLine[] {
  const cell = (r: string[], i: number) => (i >= 0 ? (r[i] ?? '') : '');
  return rows.slice(map.header ? 1 : 0).map((cells, i) => {
    const row = i + (map.header ? 2 : 1);
    const date = bankDate(cell(cells, map.date));
    const label = cell(cells, map.label).replace(/\s+/g, ' ');
    let amount: string | null;
    if (map.amount >= 0) amount = bankAmount(cell(cells, map.amount));
    else {
      const out = bankAmount(cell(cells, map.debit));
      const into = bankAmount(cell(cells, map.credit));
      amount =
        out && into
          ? null
          : out
            ? `-${out.replace(/^-/, '')}`
            : into
              ? into.replace(/^-/, '')
              : null;
    }
    const problem = !date
      ? UNDATED
      : !amount
        ? 'مبلغ غير مقروء'
        : !label
          ? 'بلا بيان'
          : null;
    const balance = bankAmount(cell(cells, map.balance));
    const reference = cell(cells, map.reference);
    return {
      row,
      cells,
      problem,
      line:
        problem || !date || !amount
          ? null
          : {
              date,
              label,
              amount,
              ...(balance ? { balance } : {}),
              ...(reference ? { reference } : {}),
            },
    };
  });
}
